"use client";

import { getUserOrganizations } from "@pex/database/queries/organizations";
import type { AppContextType, Organization } from "@pex/types";
import { useEffect, useState } from "react";
import { useAuth } from "./use-auth";

function getStoredOrgId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem("currentOrgId");
}

function setStoredOrgId(orgId: string): void {
  if (typeof window !== "undefined") {
    localStorage.setItem("currentOrgId", orgId);
  }
}

function findStoredOrg(
  orgs: Organization[],
  storedOrgId: string | null
): Organization | null {
  if (!storedOrgId) {
    return null;
  }
  return orgs.find((o) => o.id === storedOrgId) ?? null;
}

function getDefaultOrg(orgs: Organization[]): Organization | null {
  return orgs.length > 0 && orgs[0] ? orgs[0] : null;
}

function selectCurrentOrg(
  orgs: Organization[],
  setCurrentOrg: (org: Organization | null) => void
): void {
  const storedOrgId = getStoredOrgId();
  const storedOrg = findStoredOrg(orgs, storedOrgId);

  if (storedOrg) {
    setCurrentOrg(storedOrg);
    return;
  }

  const defaultOrg = getDefaultOrg(orgs);
  if (defaultOrg) {
    setCurrentOrg(defaultOrg);
    setStoredOrgId(defaultOrg.id);
  }
}

export function useOrg(appContext?: AppContextType) {
  const { user, session, supabase } = useAuth();
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [userOrgs, setUserOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!(user && session)) {
      setLoading(false);
      return;
    }

    async function fetchOrganizations() {
      if (!user) {
        return;
      }
      try {
        const orgs = await getUserOrganizations(supabase, user.id, appContext);
        setUserOrgs(orgs);
        setError(null);
        selectCurrentOrg(orgs, setCurrentOrg);
        setLoading(false);
      } catch (err) {
        setError(
          err instanceof Error
            ? err
            : new Error("Failed to fetch organizations")
        );
        setLoading(false);
      }
    }

    fetchOrganizations();
  }, [user, session, appContext, supabase]);

  const switchOrg = (orgId: string) => {
    const org = userOrgs.find((o) => o.id === orgId);
    if (org) {
      setCurrentOrg(org);
      setStoredOrgId(orgId);
    }
  };

  return {
    currentOrg,
    userOrgs,
    loading,
    error,
    switchOrg,
  };
}
